import * as React from "react";
import { ProxyService } from  "@modules/api/api";
import { TimelineSection } from "./timeline-section";
import { SearchSection } from "./search-section";
import { SearchResultsSection } from "./search-results-section";
import { OldRouteRedirector } from "./old-route-redirector";
import { useLocation, Route, Router, Switch, Redirect } from "wouter";

type SearchType = "email" | "order";

interface Props {
	allowSearch: boolean;
	shopUrl: string;
	hostDomain: string;
	proxyPathPrefix: string;
    allowSearchViaOrderNumber: boolean;
}

export function Widget(props: Props): JSX.Element {
    const [searchType, setSearchType] = React.useState<SearchType>("email");
    const [searchTerm, setSearchTerm] = React.useState("");
    const [searchError, setSearchError] = React.useState<string | null>(null);

    // Create a button and pull its background color to determine the shop's 'theme' color.
    // TODO: I believe the `Shopify` global contains some kind of theme info object?
    const themeColor = React.useMemo(() => {
        const button = document.createElement("button");
        button.classList.add("btn", "btn-primary");

        return window.getComputedStyle(button).backgroundColor;
    }, []);
    const service = React.useMemo(() => {
        return new ProxyService({
            hostDomain: props.hostDomain, 
            shopDomain: props.shopUrl, 
            proxyPathPrefix: props.proxyPathPrefix
        });
    }, [props.hostDomain, props.shopUrl, props.proxyPathPrefix]);
    const [_, redirect] = useLocation();

    // Event handlers
    function navigateToOrder(orderShopifyId: number) {
        // Change to {proxyPath}/{orderId}
        redirect(props.proxyPathPrefix + "/order/" + orderShopifyId, {
            replace: false
        });

        //window.history.replaceState({}, `Latest status for order ${orderShopifyId}.`, url);
    }

    function navigateToSearch() {
        redirect(props.proxyPathPrefix);
    }

    function navigateToSearchResults() {
        if (!searchTerm) {
            return setSearchError(searchType === "email"
                ? "You must enter an email address."
                : "You must enter an order number.");
        }

        const url = `${props.proxyPathPrefix}/search/${searchType}/${encodeURIComponent(searchTerm)}`;
        redirect(url);
    }

    return (
        <main id="proxy" className="no-bg">
            <div className="ms-grid">
                <div className="ms-row hc">
                    <Router base={props.proxyPathPrefix}>
                        <Switch>
                            <Route path="/search">
                                <SearchSection
                                    shopUrl={props.shopUrl}
                                    themeColor={themeColor}
                                    allowSearchViaOrderNumber={props.allowSearchViaOrderNumber}
                                    error={searchError}
                                    searchTerm={searchTerm}
                                    searchType={searchType}
                                    onSetSearchTerm={setSearchTerm}
                                    onSetSearchType={setSearchType}
                                    onBeginSearch={navigateToSearchResults}
                                />
                            </Route>
                            <Route path="/search/:email">
                                {params => (
                                    <Redirect to={"/search/email/" + params.email} />
                                )}
                            </Route>
                            <Route path="/search/:searchType/:searchTerm">
                                {params => (
                                    <SearchResultsSection
                                        shopUrl={props.shopUrl}
                                        themeColor={themeColor}
                                        service={service}
                                        searchType={params.searchType === "order" ? "order" : "email"}
                                        searchTerm={params.searchTerm ?? ""}
                                        onNavigateToOrder={navigateToOrder}
                                        onNavigateToSearch={navigateToSearch}
                                    />
                                )}
                            </Route>
                            <Route path="/order/:shopifyOrderId">
                                {params => (
                                    <TimelineSection
                                        shopUrl={props.shopUrl}
                                        themeColor={themeColor}
                                        allowSearch={props.allowSearch}
                                        loaded={false}
                                        onNavigateToSearch={navigateToSearch}
                                        orderShopifyId={parseInt(params.shopifyOrderId)}
                                        service={service}
                                    />
                                )}
                            </Route>
                            <Route>
                                <OldRouteRedirector />
                            </Route>
                        </Switch>
                    </Router>
                </div>
            </div>
        </main>
    );
}
